import { createStore } from './createStore';

/**
 * Overlay clips store — manages B-roll/overlay video clips on the project timeline.
 *
 * Overlay clips are positioned absolutely on the project timeline (via startMs)
 * and play full-screen over the base video track.
 */

const initialState = {
  overlayClips: [],
  selectedOverlayClipId: null,
};

export const useOverlayClipsStore = createStore('OverlayClipsStore', (set, get) => ({
  ...initialState,

  /**
   * Replace all overlay clips at once.
   * @param {Array<object>} clips
   */
  setOverlayClips: (clips) =>
    set({
      overlayClips: Array.isArray(clips)
        ? [...clips].sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0))
        : [],
    }),

  /**
   * Add a new overlay clip or update if it already exists.
   * @param {object} clip
   */
  upsertOverlayClip: (clip) =>
    set((state) => {
      if (!clip?.id) return state;
      const existingIndex = state.overlayClips.findIndex((c) => c.id === clip.id);
      if (existingIndex === -1) {
        const clips = [...state.overlayClips, clip];
        return { overlayClips: clips.sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0)) };
      }
      const existing = state.overlayClips[existingIndex];
      const merged = { ...existing, ...clip };
      const hasChanged = Object.keys(merged).some((k) => merged[k] !== existing[k]);
      if (!hasChanged) return state;
      const newClips = state.overlayClips.map((c) => (c.id === clip.id ? merged : c));
      return { overlayClips: newClips.sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0)) };
    }),

  /**
   * Apply partial updates to an overlay clip.
   * @param {string} clipId
   * @param {object} updates
   */
  updateOverlayClip: (clipId, updates) =>
    set((state) => ({
      overlayClips: state.overlayClips
        .map((clip) => (clip.id === clipId ? { ...clip, ...updates } : clip))
        .sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0)),
    })),

  /**
   * Remove an overlay clip.
   * @param {string} clipId
   */
  removeOverlayClip: (clipId) =>
    set((state) => ({
      overlayClips: state.overlayClips.filter((clip) => clip.id !== clipId),
      selectedOverlayClipId:
        state.selectedOverlayClipId === clipId ? null : state.selectedOverlayClipId,
    })),

  /**
   * Select an overlay clip (for UI focus).
   * @param {string|null} clipId
   */
  selectOverlayClip: (clipId) =>
    set({ selectedOverlayClipId: clipId }),

  /**
   * Clear all overlay clips and reset state.
   */
  resetOverlayClips: () => set(() => initialState),

  /**
   * Get overlay clip by ID.
   * @param {string} clipId
   * @returns {object|null}
   */
  getOverlayClipById: (clipId) =>
    get().overlayClips.find((clip) => clip.id === clipId) ?? null,

  /**
   * Get all overlay clips that are active at a given time (ms).
   * @param {number} timeMs
   * @returns {Array<object>}
   */
  getActiveOverlaysAtTime: (timeMs) =>
    get().overlayClips.filter((clip) => {
      const start = clip.startMs ?? 0;
      const end = start + (clip.durationMs ?? 0);
      return clip.status === 'completed' && clip.videoUrl && timeMs >= start && timeMs < end;
    }),
}));
